import React from 'react';
import PropTypes from 'prop-types';
import { formatBigNumber } from '../../../SleepHub/sleepHubApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  PANEL_ROW_CLASS,
  STAT_ICONS
} from '../../../../component/common/designSystem';
import { DIFFICULTY_RAMP, SEQUENCE_RAMP } from './gamesTheme';
import {
  useGameDetail,
  orDash,
  asDuration,
  TrendPanel,
  WeekdayPanel,
  GameFootnote,
  BandBar
} from './GameShell';

/**
 * Games → Block Fill.
 *
 * A play here is a LEVEL CLEARED — one row per completion, which makes this the
 * best-instrumented of the four games and also the one whose play count runs
 * away fastest. A level takes seconds, so "Block Fill has the most plays" is
 * usually a statement about level length rather than about engagement. The
 * player count and the days-played figure are the ones to read.
 *
 * TWO DIFFERENT NOTIONS OF "LEVELS DONE" LIVE ON THIS PAGE, deliberately:
 *
 *   Levels cleared    every completion row, replays included
 *   How far people get  DISTINCT levels per person
 *
 * Replaying a level you have already beaten is not progress, and the app's own
 * leaderboard counts distinct levels for the same reason. Showing both makes
 * the gap between them visible, and that gap is itself informative — a large
 * one means people are grinding levels they have already finished.
 *
 * THE DIFFICULTY TABLE IS THE DESIGN CHECK. If `hard` is not measurably slower
 * than `medium`, the tiers are not doing what they claim.
 *
 * "Where people are now" comes from the progress document rather than the
 * completion log: it is the tier each person last chose, which is the closest
 * thing this game has to a stated preference.
 */
const BlockFillPage = ({ fetcher, populationLabel = 'users' }) => {
  const [stats, gate] = useGameDetail(fetcher);
  if (gate) return gate;

  return (
    <div>
      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        <StatCard
          icon={STAT_ICONS.users}
          title="People Playing"
          value={formatBigNumber(stats?.players || 0)}
        />
        <StatCard
          icon={STAT_ICONS.completions}
          title="Levels Cleared"
          value={formatBigNumber(stats?.plays || 0)}
        />
        {/* Distinct levels across everyone — how much of the game has actually
            been seen, as opposed to how many times it has been replayed. */}
        <StatCard
          icon={STAT_ICONS.catalog}
          title="Different Levels Seen"
          value={formatBigNumber(stats?.distinctLevels || 0)}
        />
        <StatCard
          icon={STAT_ICONS.sessions}
          title="Levels Each"
          value={stats?.playsPerPlayer ?? 0}
        />
        <StatCard
          icon={STAT_ICONS.time}
          title="Average Level"
          value={asDuration(stats?.avgSeconds)}
        />
        <StatCard
          icon={STAT_ICONS.streak}
          title="Days Played On"
          value={formatBigNumber(stats?.activeDays || 0)}
        />
      </div>

      {/* ---------- Difficulty and time ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <MiniDonutCard
          title="Which Difficulty"
          subtitle="Levels cleared, by tier"
          data={stats?.difficultySplit || []}
          colors={DIFFICULTY_RAMP}
          unit="levels"
          totalLabel="Levels"
          legendLimit={3}
        />
        <MiniDonutCard
          title="Where People Are Now"
          subtitle="The tier each player last chose"
          data={stats?.currentTier || []}
          colors={DIFFICULTY_RAMP}
          unit="people"
          totalLabel="Players"
          legendLimit={3}
        />
        <Panel title="How Long a Level Takes" subtitle="Time to clear one level">
          {(stats?.timeSplit || []).some((row) => row.count > 0) ? (
            <BandBar rows={stats.timeSplit} colors={SEQUENCE_RAMP} unit="levels" />
          ) : (
            <PanelEmpty>No levels cleared yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Progress vs grind ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <MiniDonutCard
          title="How Far People Get"
          subtitle="Distinct levels cleared per person — replays not counted"
          data={stats?.reachSplit || []}
          colors={SEQUENCE_RAMP}
          unit="people"
          totalLabel="Players"
          legendLimit={5}
        />
        <MiniDonutCard
          title="How Much People Play"
          subtitle="Every completion per person, replays included"
          data={stats?.volumeSplit || []}
          colors={SEQUENCE_RAMP}
          unit="people"
          totalLabel="Players"
          legendLimit={5}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
          <StatCard
            icon={STAT_ICONS.rating}
            title="Levels Using a Hint"
            value={`${stats?.hintRate ?? 0}%`}
          />
          <StatCard
            icon={STAT_ICONS.time}
            title="Time Spent Playing"
            value={`${formatBigNumber(stats?.totalMinutes || 0)} min`}
          />
          <StatCard
            icon={STAT_ICONS.activeUsers}
            title={`Share of ${populationLabel}`}
            value={
              stats?.totalUsers > 0
                ? `${Math.round(((stats.players || 0) / stats.totalUsers) * 1000) / 10}%`
                : '-'
            }
          />
        </div>
      </div>

      {/* ---------- Do the tiers mean anything ---------- */}
      <div className="mt-6">
        <Panel
          title="Do the Tiers Mean Anything?"
          subtitle="Hard should take measurably longer than medium — a dash means too few levels to report"
        >
          {(stats?.difficultyTable || []).length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Tier</th>
                  <th className="py-2 px-4 font-medium text-center">Levels cleared</th>
                  <th className="py-2 px-4 font-medium text-center">People</th>
                  <th className="py-2 px-4 font-medium text-center">Average time</th>
                  <th className="py-2 font-medium text-right">Used a hint</th>
                </>
              }
            >
              {stats.difficultyTable.map((row) => (
                <tr key={row.label} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">{row.label}</td>
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.completions)}</td>
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.players)}</td>
                  <td className="py-2 px-4 text-center">{asDuration(row.avgSeconds)}</td>
                  <td className="py-2 text-right">
                    {row.hintRate == null ? '-' : `${row.hintRate}%`}
                  </td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>No tier has enough levels cleared to report.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Rhythm ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <TrendPanel stats={stats} subtitle="Levels cleared per day — last 30 days" />
        <WeekdayPanel stats={stats} />
      </div>

      <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
        A Block Fill level takes seconds, so this game&apos;s play count is usually the largest of
        the four without meaning the most time was spent on it. Players and days played are the
        figures to compare across games. Hints per level: {orDash(stats?.hintsPerLevel)}.
      </p>

      <GameFootnote />
    </div>
  );
};

BlockFillPage.propTypes = {
  fetcher: PropTypes.func.isRequired,
  populationLabel: PropTypes.string
};

export default BlockFillPage;
